// src/lib/page-titles.ts
//
// THE name of every page — one place, read by the sidebar (config/nav.ts),
// the top bar breadcrumb (components/layout/Topbar.tsx) and the browser tab
// (components/layout/DocumentTitle.tsx).
//
// Why it exists: names used to come from four places — a Media-Pulse-only
// table here, a second table inside Topbar, the sidebar's own strings, and
// finally the last URL segment as-is. 42 of 62 pages showed English in the top
// bar ("dashboard", "tv live", "[grid]") and 31 disagreed with the sidebar.
//
// Naming policy (agreed 2026-09-29):
//   - Arabic, always. A page never falls back to its URL.
//   - Nouns, not commands: «تفريغ ملف محلي», not «ابدأ تفريغ ملف محلي».
//   - One name per page, identical in sidebar, top bar and browser tab.
//     The <h1> inside a page may be longer and more descriptive.
//   - A page outside the menu names its `parent`, which becomes the middle
//     step of the breadcrumb: «مهام العمل › الأدوات الذكية › دمج وتقسيم PDF».
//
// Dynamic segments are written as in the app folder: "/tv-live/wall/[grid]".
// Adding a page? Add it here — src/__tests__/page-names.test.ts fails until you do.

export const SITE_NAME = "مركز الإعلام الذكي";

export interface PageName {
  title: string;
  /** Registered path of the page this one sits under (breadcrumb middle step). */
  parent?: string;
}

export const PAGE_NAMES: Record<string, PageName> = {
  // ── الرئيسية ──
  "/dashboard": { title: "الرئيسية" },
  "/profile": { title: "الملف الشخصي" },
  "/login": { title: "تسجيل الدخول" },

  // ── مهام العمل ──
  "/jobs": { title: "أرشيف الملفات المفرغة" },
  "/jobs/stats": { title: "إحصاءات التفريغ", parent: "/jobs" },
  "/jobs/new": { title: "تفريغ ملف محلي" },
  "/live": { title: "تفريغ بث مباشر" },
  "/live/archive": { title: "أرشيف تفريغ المباشر" },
  "/live/voices": { title: "مكتبة الأصوات" },
  "/tv-live": { title: "إدارة القنوات الفضائية" },
  "/tv-live/screen": { title: "شبكات تفريغ الفضائيات" },
  "/tv-live/screen/[grid]": { title: "شبكة تفريغ الفضائيات", parent: "/tv-live/screen" },
  "/tv-live/grids/[id]/control": { title: "التحكّم بالشبكة المُدارة", parent: "/tv-live/screen" },
  "/radio-live": { title: "إدارة القنوات الإذاعية" },
  "/radio-live/screen": { title: "شبكات تفريغ الإذاعات" },
  "/radio-live/screen/[grid]": { title: "شبكة تفريغ الإذاعات", parent: "/radio-live/screen" },
  "/radio-live/grids/[id]/control": { title: "التحكّم بالشبكة المُدارة", parent: "/radio-live/screen" },
  "/scheduled-recordings": { title: "التسجيلات المجدولة" },
  "/alerts": { title: "التنبيهات" },
  "/live/running": { title: "الجلسات الجارية" },
  "/infographics": { title: "الإنفوجرافيك" },
  "/political": { title: "التحليل السياسي" },
  "/translate": { title: "الجمع والترجمة" },
  "/news": { title: "جمع الأخبار" },
  "/articles": { title: "محرر المقالات" },
  "/campaigns": { title: "إدارة الحملات" },
  "/campaigns/settings": { title: "خيارات الحملات", parent: "/campaigns" },
  "/campaigns/[id]/dashboard": { title: "لوحة الحملة", parent: "/campaigns" },
  "/reports": { title: "التقارير الموحّدة" },
  "/smart-tools": { title: "الأدوات الذكية" },
  "/smart-tools/arabic-text-cleaner": { title: "منظف النصوص العربية", parent: "/smart-tools" },
  "/smart-tools/pdf-download": { title: "تنزيل ملف PDF", parent: "/smart-tools" },
  "/smart-tools/pdf-editor": { title: "تعديل ملف PDF", parent: "/smart-tools" },
  "/smart-tools/pdf-extract-text": { title: "استخراج النص من PDF", parent: "/smart-tools" },
  "/smart-tools/pdf-merge-split": { title: "دمج وتقسيم PDF", parent: "/smart-tools" },

  // ── ميديا بلص ──
  "/media-pulse/guide": { title: "دليل سير العمل" },
  "/media-pulse/dashboard": { title: "نظرة تحليلية" },
  "/media-pulse/concerns": { title: "الاهتمامات" },
  "/media-pulse/sources": { title: "المصادر والجمع" },
  "/media-pulse/collect-sessions": { title: "أرشيف الجمع التلقائي" },
  "/media-pulse/search": { title: "البحث في المحتوى" },
  "/media-pulse/monitoring": { title: "المراقبة والمتابعة" },
  "/media-pulse/monitor-rules": { title: "قواعد الرصد التلقائي" },
  "/media-pulse/alerts": { title: "التنبيهات والمخاطر" },
  "/media-pulse/rumors": { title: "متابعة الشائعات" },
  "/media-pulse/media-watch": { title: "رصد السوشيال" },
  "/report": { title: "الإبلاغ عن محتوى" },  // عامّة بلا دخول — قناة البلاغات
  "/extension-privacy": { title: "سياسة خصوصيّة الإضافة" },  // عامّة بلا دخول — يشترطها سوق Chrome
  "/media-pulse/influencers": { title: "متابعة المؤثرين" },
  "/media-pulse/campaigns": { title: "متابعة الحملات" },
  "/media-pulse/story-groups": { title: "مجموعات القصص" },
  "/media-pulse/ai-processing": { title: "المعالجة الذكية" },
  "/media-pulse/reports": { title: "تقارير الرصد" },
  "/media-pulse/sessions": { title: "جلسات بحث موجَّهة" },
  "/media-pulse/settings": { title: "إعدادات ميديا بلص" },

  // ── الإدارة والتحكم ──
  "/admin/settings": { title: "الإعدادات" },
  "/admin/users": { title: "المستخدمون" },
  "/admin/permissions": { title: "الصلاحيات" },
  "/admin/system": { title: "معلومات النظام" },
  "/admin/operations": { title: "دليل التشغيل" },
  "/admin/usage": { title: "الاستعمال والكلفة" },
  "/admin/audit-log": { title: "سجل النشاط" },
  "/admin/database": { title: "قاعدة البيانات" },
  "/admin/trash": { title: "سلة المحذوفات" },
  "/admin/servers": { title: "السيرفرات" },
  "/coming-soon": { title: "قيد التطوير" },
};

/**
 * Pages that only redirect elsewhere and never render a screen of their own —
 * they need no name. Kept explicit so the guard test can tell "no name on
 * purpose" from "forgot to add a name".
 */
export const REDIRECT_ONLY = ["/", "/media-pulse", "/smart-tools/downloader"];

/** Name of a registered page, for the sidebar. Throws on a typo'd href. */
export function pageName(href: string): string {
  const entry = PAGE_NAMES[href];
  if (!entry) throw new Error(`page-titles: no name registered for "${href}"`);
  return entry.title;
}

function clean(pathname: string): string {
  const p = (pathname || "/").split("?")[0].split("#")[0].replace(/\/+$/, "");
  return p || "/";
}

function matches(pattern: string, path: string): boolean {
  const a = pattern.split("/");
  const b = path.split("/");
  return a.length === b.length && a.every((seg, i) => /^\[.+\]$/.test(seg) || seg === b[i]);
}

/** The registered key for a concrete path: exact, then dynamic pattern. */
export function pageKey(pathname: string): string | null {
  const path = clean(pathname);
  if (PAGE_NAMES[path]) return path;
  return Object.keys(PAGE_NAMES).find((k) => k.includes("[") && matches(k, path)) ?? null;
}

/**
 * The page to name for a path. An unregistered sub-path takes its nearest
 * registered ancestor — never its own URL segment.
 */
export function resolvePage(pathname: string): { key: string; title: string } | null {
  let path = clean(pathname);
  while (true) {
    const key = pageKey(path);
    if (key) return { key, title: PAGE_NAMES[key].title };
    if (path === "/") return null;
    path = path.slice(0, path.lastIndexOf("/")) || "/";
  }
}

/** Breadcrumb steps below the section: registered parents first, the page last. */
export function pageTrail(pathname: string): { key: string; title: string }[] {
  const page = resolvePage(pathname);
  if (!page) return [];
  const trail = [page];
  const seen = new Set([page.key]);
  let parent = PAGE_NAMES[page.key].parent;
  while (parent && PAGE_NAMES[parent] && !seen.has(parent)) {
    trail.unshift({ key: parent, title: PAGE_NAMES[parent].title });
    seen.add(parent);
    parent = PAGE_NAMES[parent].parent;
  }
  return trail;
}
